import React, { FC, useEffect, useState } from 'react';
import { Modal, Paper, Box, Fade, Backdrop, CircularProgress, Tabs, Tab, Typography, Chip, Link } from '@mui/material';
import WatchIcon from '@mui/icons-material/Watch';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { format, addSeconds } from 'date-fns';

import TrainingModalSummary from '../coe-training-modal-v2/components/training-modal-summary';
import DescriptionTab from '../coe-training-modal-v2/tabs/description.tab';
import AnalyticsTab from '../coe-training-modal-v2/tabs/analytics.tab';
import LogTab from '../coe-training-modal-v2/tabs/log.tab';
import FormsTab from '../coe-training-modal-v2/tabs/forms.tab';
import EquipmentTab from '../coe-training-modal-v2/tabs/equipment.tab';
import CoachAthleteModalHeader, { CoachAthleteMemberPreview } from './components/coach-athlete-modal-header';
import CoachAthleteCommentsTab from './tabs/coach-athlete-comments.tab';
import useService from '../../hooks/service.hook';
import TrainingsService from '../../services/trainings.service';
import CustomTrainingFormsService from '../../services/custom-training-forms.service';
import { Training, CustomTrainingForm, TrainingTeamAssignee } from '../../models/training.models';
import { palette } from '../../theme';
import './coach-athlete-training-modal.css';

export interface CoachAthleteTrainingModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  memberId: string | number;
  trainingId: string | number | null;
  memberPreview: CoachAthleteMemberPreview;
  onTrainingUpdated?: () => void;
}

type TabType = 'description' | 'analytics' | 'log' | 'forms' | 'comments' | 'equipment';

interface TabConfig {
  type: TabType;
  label: string | React.ReactNode;
  value: number;
}

function formatTimeRange(startTimeStr: string, totalDurationSeconds?: number): string {
  const start = new Date(startTimeStr);
  const startFormatted = format(start, 'h:mm aaa');
  if (!totalDurationSeconds) return startFormatted;
  const end = addSeconds(start, totalDurationSeconds);
  return `${startFormatted} – ${format(end, 'h:mm aaa')}`;
}

const CoachAthleteTrainingModal: FC<CoachAthleteTrainingModalProps> = ({
  open,
  onClose,
  teamId,
  memberId,
  trainingId,
  memberPreview,
  onTrainingUpdated,
}) => {
  const navigate = useNavigate();
  const trainingsService = useService(TrainingsService);
  const customFormsService = useService(CustomTrainingFormsService);
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [training, setTraining] = useState<Training | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [commentsWereMarkedAsRead, setCommentsWereMarkedAsRead] = useState(false);
  const [attachedForm, setAttachedForm] = useState<CustomTrainingForm | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (open && teamId && memberId && trainingId) {
      setCommentsWereMarkedAsRead(false);
      fetchTraining();
    }
  }, [open, teamId, memberId, trainingId]);

  const closeModal = () => {
    const shouldNotifyParent = commentsWereMarkedAsRead;
    setCommentsWereMarkedAsRead(false);
    setTraining(null);
    setActiveTab(0);
    onClose();
    if (shouldNotifyParent && onTrainingUpdated) {
      onTrainingUpdated();
    }
  };

  const fetchFormDetails = async (formTeamId: number, formId: number) => {
    setFormLoading(true);
    try {
      const response = await customFormsService.getForm(String(formTeamId), formId);
      setAttachedForm(response.data.data);
    } catch {
      setAttachedForm(null);
    } finally {
      setFormLoading(false);
    }
  };

  const fetchTraining = async (preserveTab = false) => {
    if (!trainingId) return;

    setLoading(true);
    try {
      const response = await trainingsService.getAthleteTrainingForCoach(teamId, memberId, trainingId);
      const trainingData = response.data.data as Training;
      setTraining(trainingData);
      if (!preserveTab) {
        setActiveTab(0);
      }

      if (trainingData?.trainingFormId && trainingData?.team?.id) {
        await fetchFormDetails(trainingData.team.id, trainingData.trainingFormId);
      } else {
        setAttachedForm(null);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        enqueueSnackbar('You do not have permission to view this training.', { variant: 'error' });
      } else if (status === 404) {
        enqueueSnackbar('Training not found.', { variant: 'error' });
      } else {
        enqueueSnackbar('There was an error while fetching this training. Please try again later.', {
          variant: 'error',
        });
      }
      closeModal();
    } finally {
      setLoading(false);
    }
  };

  const clearUnreadCommentsCount = () => {
    if (training) {
      setTraining({ ...training, unreadComments: 0 });
      setCommentsWereMarkedAsRead(true);
    }
  };

  const buildTabsConfig = (): TabConfig[] => {
    if (!training) return [];

    const tabs: TabConfig[] = [];
    let currentIndex = 0;
    const hasWorkoutSamples = (training.workoutData?.samples?.length ?? 0) > 0;
    const hasForm = !!training.trainingFormId;

    tabs.push({ type: 'description', label: 'Description', value: currentIndex++ });

    if (hasForm) {
      tabs.push({ type: 'forms', label: 'Forms', value: currentIndex++ });
    }

    if (hasWorkoutSamples) {
      tabs.push({ type: 'analytics', label: 'Analytics', value: currentIndex++ });
    } else {
      tabs.push({ type: 'log', label: 'Log', value: currentIndex++ });
    }

    const commentsLabel = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>Notes</span>
        {(training.unreadComments ?? 0) > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: palette.success.main,
              backgroundColor: palette.success.light,
              borderRadius: '8px',
              padding: '2px 6px',
              minWidth: '16px',
              textAlign: 'center',
            }}
          >
            {training.unreadComments}
          </Typography>
        )}
      </Box>
    );
    tabs.push({ type: 'comments', label: commentsLabel, value: currentIndex++ });
    tabs.push({ type: 'equipment', label: 'Equipment', value: currentIndex++ });

    return tabs;
  };

  const memberAsAssignee = (): TrainingTeamAssignee => ({
    memberId: typeof memberId === 'string' ? parseInt(memberId, 10) : memberId,
    name: memberPreview.name,
    profileImage: memberPreview.profileImage,
    duration: training?.totalDurationComplete ?? 0,
    distance: training?.totalDistanceComplete ?? 0,
    complete: training?.workoutData?.complete ?? false,
    rsvpStatus: training?.workoutData?.rsvp ?? '',
    attended: false,
    effortRating: training?.workoutData?.effortRating ?? null,
  });

  const renderTabContent = () => {
    if (!training || !trainingId) return null;

    const tabsConfig = buildTabsConfig();
    const activeTabConfig = tabsConfig.find((tab) => tab.value === activeTab);
    if (!activeTabConfig) return null;

    const numericTrainingId = typeof trainingId === 'string' ? parseInt(trainingId, 10) : trainingId;

    switch (activeTabConfig.type) {
      case 'description':
        return <DescriptionTab description={training.description || ''} />;
      case 'analytics':
        return <AnalyticsTab workoutData={training.workoutData || null} />;
      case 'log':
        return <LogTab training={training} trainingType="coach" readOnly />;
      case 'forms':
        return (
          <FormsTab
            form={attachedForm}
            loading={formLoading}
            teamMembers={[memberAsAssignee()]}
            disabled
            trainingId={numericTrainingId}
          />
        );
      case 'comments':
        return (
          <CoachAthleteCommentsTab
            trainingId={String(trainingId)}
            memberId={memberId}
            effortRating={training.workoutData?.effortRating}
            onUnreadCommentsCleared={clearUnreadCommentsCount}
          />
        );
      case 'equipment':
        return (
          <EquipmentTab
            workoutData={training.workoutData || null}
            trainingId={numericTrainingId}
            trainingType="guardian"
          />
        );
      default:
        return null;
    }
  };

  const renderTrainingBody = (trainingData: Training) => {
    const isComplete = trainingData.workoutData?.complete ?? false;
    const hasSamples = (trainingData.workoutData?.samples?.length ?? 0) > 0;

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 2.5,
            pt: 2,
            mb: 0.5,
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3, maxWidth: '75%' }}>
            {trainingData.title}
          </Typography>
          <Chip
            label={isComplete ? 'Completed' : 'Planned'}
            size="small"
            sx={{
              bgcolor: isComplete ? palette.success.light : palette.primary.light,
              color: isComplete ? palette.success.dark : palette.primary.main,
              fontWeight: 600,
              fontSize: '12px',
              borderRadius: '8px',
              mt: 0.25,
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {[
              trainingData.location,
              trainingData.time ? formatTimeRange(trainingData.time, trainingData.totalDurationPlanned) : 'Time TBD',
            ]
              .filter(Boolean)
              .join(' • ')}
          </Typography>
          {trainingData.actualStartTime && (
            <Typography variant="body2" color="primary">
              {format(new Date(trainingData.actualStartTime), 'h:mm aaa')}
            </Typography>
          )}
          {hasSamples && <WatchIcon color="primary" fontSize="small" />}
        </Box>

        <TrainingModalSummary training={trainingData} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2.5,
            mb: 2,
          }}
        >
          {trainingData.sport && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: palette.grey[100],
                borderRadius: '10px',
                px: 1.5,
                py: 0.75,
                minWidth: 60,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.2 }}
              >
                Sport
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {trainingData.sport}
              </Typography>
            </Box>
          )}
          {trainingData.sportType && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: palette.grey[100],
                borderRadius: '10px',
                px: 1.5,
                py: 0.75,
                minWidth: 60,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.2 }}
              >
                Type
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {trainingData.sportType}
              </Typography>
            </Box>
          )}
          {trainingData.focus && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: palette.grey[100],
                borderRadius: '10px',
                px: 1.5,
                py: 0.75,
                minWidth: 60,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.2 }}
              >
                Focus
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {trainingData.focus}
              </Typography>
              {trainingData.focus === 'Race' && trainingData.team?.id != null && (
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  color={palette.primary.main}
                  onClick={() => navigate(`/teams/${trainingData.team?.id}/race-log`)}
                  sx={{ '&:hover': { opacity: 0.85 } }}
                >
                  View Race Log
                </Link>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '.MuiTab-root': {
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'none',
                color: palette.text.secondary,
                '&:hover': { color: palette.text.primary },
              },
              '.Mui-selected': {
                color: palette.primary.main,
                fontWeight: 600,
              },
              '.MuiTabs-indicator': {
                backgroundColor: palette.primary.main,
                height: 3,
              },
            }}
          >
            {buildTabsConfig().map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ backgroundColor: palette.background.subtle }}>{renderTabContent()}</Box>
      </>
    );
  };

  if (!trainingId) return null;

  return (
    <Modal
      open={open}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500, onClick: closeModal }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1400,
      }}
    >
      <Fade in={open} timeout={500}>
        <Paper
          sx={{
            width: '800px',
            height: '95vh',
            backgroundColor: palette.background.paper,
            borderRadius: '25px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative',
            outline: 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {loading ? (
            <Box
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CircularProgress size={60} />
            </Box>
          ) : training ? (
            <>
              <CoachAthleteModalHeader
                memberPreview={memberPreview}
                teamName={training.team?.name}
                groupName={training.group?.name}
                onClose={closeModal}
              />
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {renderTrainingBody(training)}
              </Box>
            </>
          ) : (
            <Box
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography color="text.secondary">No training data available</Typography>
            </Box>
          )}
        </Paper>
      </Fade>
    </Modal>
  );
};

export default CoachAthleteTrainingModal;
